import { useCallback } from 'react';
import { apiHooks } from '../../../hooks/useRestAPIHooks';
import { Folder, ModelUtils, rootFolder, TemplatingItem } from '../models';

import {
  InternalQueryKeyLike,
  useInvalidateTemplatingItems,
} from './useInvalidateTemplatingItems';
import { useQueryClient } from '@tanstack/react-query';
import {
  AI_TEMPLATES_ROUTE_QUERY_KEY,
  TemplatingItemSummariesQueryResult,
} from './useTemplatingItemSummaries';
import { isInternalQueryKey } from '@lifeomic/one-query';
import { useSelectedTemplatingItem } from './useSelectedTemplatingItem';

export const useMoveTemplatingItem = () => {
  const invalidateTemplatingItems = useInvalidateTemplatingItems();
  const queryClient = useQueryClient();
  const {
    setSelectedTemplatingItemId,
    selectedTemplatingItemQuery: { data: selectedTemplatingItem },
  } = useSelectedTemplatingItem();

  const { mutateAsync } = apiHooks.useAPIMutation('PATCH /v1/ai-templates/:id');

  const moveTemplatingItem = useCallback(
    async (
      templatingItem: TemplatingItem,
      destination: Folder = rootFolder,
    ) => {
      const movedTemplatingItem = ModelUtils.moveToFolder(
        templatingItem,
        destination,
      );

      if (ModelUtils.hasSamePath(movedTemplatingItem, templatingItem)) {
        return;
      }

      queryClient.setQueriesData(
        {
          predicate: query => {
            const internalQueryKey = query.queryKey.find(
              queryKey =>
                queryKey != null &&
                typeof queryKey === 'object' &&
                isInternalQueryKey(queryKey),
            ) as InternalQueryKeyLike | undefined;
            if (!internalQueryKey) {
              return false;
            }
            if (internalQueryKey.route !== AI_TEMPLATES_ROUTE_QUERY_KEY) {
              return false;
            }
            const payload = internalQueryKey.payload as { prefix: string };
            return (
              payload.prefix === ModelUtils.getParentFolder(templatingItem).path
            );
          },
        },
        (oldData: TemplatingItemSummariesQueryResult | undefined) => {
          if (!oldData?.pages) return oldData;
          return {
            ...oldData,
            pages: oldData.pages.map(({ items, ...rest }) => ({
              items: items.filter(
                item => item.type === 'folder' || item.id !== templatingItem.id,
              ),
              ...rest,
            })),
          };
        },
      );

      return mutateAsync(
        {
          type: movedTemplatingItem.type,
          id: templatingItem.id,
          path: movedTemplatingItem.path,
        },
        {
          onSettled: () => {
            invalidateTemplatingItems(templatingItem, destination);
          },
          onSuccess: ({ template: movedTemplatingItemRaw }) => {
            const movedTemplatingItem =
              ModelUtils.templatingItemDetailFromRawData(
                movedTemplatingItemRaw,
              );
            if (
              selectedTemplatingItem?.durableId === templatingItem.durableId
            ) {
              setSelectedTemplatingItemId(movedTemplatingItem.durableId);
            }
          },
        },
      );
    },
    [
      mutateAsync,
      invalidateTemplatingItems,
      queryClient,
      selectedTemplatingItem,
      setSelectedTemplatingItemId,
    ],
  );

  return {
    moveTemplatingItem,
  };
};
